/* ============================================================
   Varniya — Diamond Concierge
   Instant search + conversational FAQ controller.
   Vanilla JS, no dependencies. Supports multiple instances.

   DATA SOURCE ABSTRACTION
   -----------------------
   The UI never talks to a data store directly. It calls
   Concierge.provider.search(query)  ->  [faq,...]
   Concierge.provider.get(slug)      ->  faq
   Today these read a static index rendered by Liquid from
   Shopify metaobjects. To move to an LLM/API later, swap the
   provider for one that returns the SAME faq shape — no UI change.
   ============================================================ */
(function () {
  "use strict";

  var STRIP = document.createElement("div");
  function decode(html) { STRIP.innerHTML = html || ""; return STRIP.textContent || ""; }

  /* ---------- Rich-text (Shopify RichText JSON) -> HTML ----------
     Answers are stored as Shopify rich_text JSON. Rendering here (rather
     than relying on Liquid) keeps the interactive UI correct and is the
     same format a future API/LLM would return. Accepts JSON string,
     parsed object, or plain HTML (passed through untouched). */
  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function rtChildren(nodes) {
    return (nodes || []).map(rtNode).join("");
  }
  function rtNode(n) {
    if (!n || typeof n !== "object") return "";
    switch (n.type) {
      case "root": return rtChildren(n.children);
      case "paragraph": return "<p>" + rtChildren(n.children) + "</p>";
      case "heading": {
        var lvl = Math.min(6, Math.max(1, n.level || 3));
        return "<h" + lvl + ">" + rtChildren(n.children) + "</h" + lvl + ">";
      }
      case "list": {
        var tag = n.listType === "ordered" ? "ol" : "ul";
        return "<" + tag + ">" + rtChildren(n.children) + "</" + tag + ">";
      }
      case "list-item": return "<li>" + rtChildren(n.children) + "</li>";
      case "link": {
        var href = escapeHtml(n.url || "#");
        var title = n.title ? ' title="' + escapeHtml(n.title) + '"' : "";
        return '<a href="' + href + '"' + title + ' rel="noopener">' + rtChildren(n.children) + "</a>";
      }
      case "text": {
        var t = escapeHtml(n.value);
        if (n.bold) t = "<strong>" + t + "</strong>";
        if (n.italic) t = "<em>" + t + "</em>";
        return t;
      }
      default: return n.children ? rtChildren(n.children) : (n.value ? escapeHtml(n.value) : "");
    }
  }
  function renderRichText(input) {
    if (input == null) return "";
    var obj = input;
    if (typeof input === "string") {
      var s = input.trim();
      if (s.charAt(0) !== "{" && s.charAt(0) !== "[") return input; // already HTML/plain
      try { obj = JSON.parse(s); } catch (e) { return input; }
    }
    if (obj && obj.type === "root") return rtChildren(obj.children);
    return typeof input === "string" ? input : "";
  }

  /* ---------- Search provider over a static index ---------- */
  function StaticProvider(faqs) {
    this.faqs = faqs;
    this.bySlug = {};
    for (var i = 0; i < faqs.length; i++) {
      this.bySlug[faqs[i].slug] = faqs[i];
      faqs[i]._html = renderRichText(faqs[i].answer);
      faqs[i]._text = decode(faqs[i]._html).toLowerCase();
    }
  }
  StaticProvider.prototype.get = function (slug) { return this.bySlug[slug] || null; };
  StaticProvider.prototype.all = function () { return this.faqs; };
  StaticProvider.prototype.search = function (q) {
    q = (q || "").trim().toLowerCase();
    if (!q) return [];
    var tokens = q.split(/\s+/).filter(Boolean);
    var out = [];
    for (var i = 0; i < this.faqs.length; i++) {
      var f = this.faqs[i];
      var ql = f.question.toLowerCase();
      var kw = (f.keywords || "").toLowerCase();
      var tg = (f.tags || []).join(" ").toLowerCase();
      var ct = (f.categoryTitle || "").toLowerCase();
      var score = 0;
      for (var t = 0; t < tokens.length; t++) {
        var tok = tokens[t];
        if (ql.indexOf(tok) !== -1) score += ql.indexOf(tok) === 0 ? 12 : 8;
        if (kw.indexOf(tok) !== -1) score += 5;
        if (tg.indexOf(tok) !== -1) score += 4;
        if (ct.indexOf(tok) !== -1) score += 3;
        if (f._text.indexOf(tok) !== -1) score += 2;
      }
      if (ql.indexOf(q) !== -1) score += 6;            // whole-phrase boost
      if (score > 0) { score += (f.priority || 0) / 100; out.push({ f: f, s: score }); }
    }
    out.sort(function (a, b) { return b.s - a.s; });
    return out.slice(0, 8).map(function (r) { return r.f; });
  };

  /* ---------- helpers ---------- */
  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }
  function ico(id) { return '<svg class="dc-ico" aria-hidden="true" focusable="false"><use href="#dc-i-' + id + '"></use></svg>'; }
  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function highlight(text, q) {
    if (!q) return esc(text);
    var tokens = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!tokens.length) return esc(text);
    var re = new RegExp("(" + tokens.map(function (t) { return t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"); }).join("|") + ")", "ig");
    return esc(text).replace(re, "<mark>$1</mark>");
  }

  /* ---------- Controller ---------- */
  function Concierge(root) {
    this.root = root;
    var raw = root.querySelector("[data-dc-data]");
    var cfgEl = root.querySelector("[data-dc-config]");
    this.config = cfgEl ? JSON.parse(cfgEl.textContent) : {};
    this.config.reduceMotion = !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    var data = { faqs: [], categories: [] };
    try { data = JSON.parse(raw.textContent); } catch (e) { data = { faqs: [], categories: [] }; }

    this.categories = data.categories || [];
    this.catBySlug = {};
    for (var c = 0; c < this.categories.length; c++) this.catBySlug[this.categories[c].slug] = this.categories[c];
    // decorate faqs with category title for search + labels
    var faqs = data.faqs || [];
    for (var i = 0; i < faqs.length; i++) {
      var cat = this.catBySlug[faqs[i].category];
      faqs[i].categoryTitle = cat ? cat.title : faqs[i].category;
    }
    this.provider = new StaticProvider(faqs);

    this.$ = {
      search: root.querySelector("[data-dc-search]"),
      searchWrap: root.querySelector("[data-dc-search-wrap]"),
      clear: root.querySelector("[data-dc-clear]"),
      results: root.querySelector("[data-dc-results]"),
      viewHome: root.querySelector('[data-dc-view="home"]'),
      viewConvo: root.querySelector('[data-dc-view="convo"]'),
      convo: root.querySelector("[data-dc-convo]"),
      convoBar: root.querySelector("[data-dc-convo-bar]"),
      composer: root.querySelector("[data-dc-composer]"),
      composerInput: root.querySelector("[data-dc-composer-input]"),
      send: root.querySelector("[data-dc-send]"),
      scroll: root.querySelector("[data-dc-scroll]"),
      back: root.querySelector("[data-dc-back]"),
      live: root.querySelector("[data-dc-live]")
    };

    this.activeIndex = -1;
    this.currentResults = [];
    this.asked = {};   // slugs already asked in this conversation
    this.bind();
    root.classList.add("dc-ready");
    this.reveal();
    this.handleHash();
  }

  Concierge.prototype.reveal = function () {
    // stagger entrance handled by CSS via data-reveal
  };

  Concierge.prototype.bind = function () {
    var self = this, $ = this.$;

    // --- Search input: instant ---
    if ($.search) {
      $.search.addEventListener("input", function () { self.onSearch(); });
      $.search.addEventListener("focus", function () { if ($.search.value.trim()) self.openResults(); });
      $.search.addEventListener("keydown", function (e) { self.onSearchKey(e); });
    }
    if ($.clear) $.clear.addEventListener("click", function () { self.clearSearch(); $.search.focus(); });

    // Close results on outside click / Escape
    document.addEventListener("click", function (e) {
      if ($.searchWrap && !$.searchWrap.contains(e.target)) self.closeResults();
    });

    // --- Topics, chips, categories, related (event delegation) ---
    this.root.addEventListener("click", function (e) {
      var a = e.target.closest("[data-dc-ask]");
      if (a) { e.preventDefault(); self.ask(a.getAttribute("data-dc-ask"), true); return; }
      var cat = e.target.closest("[data-dc-cat]");
      if (cat) { e.preventDefault(); self.openCategory(cat.getAttribute("data-dc-cat")); return; }
    });

    // --- Back ---
    if ($.back) $.back.addEventListener("click", function () { self.goHome(); });

    // --- Composer (free text) ---
    if ($.composer) {
      $.composer.addEventListener("submit", function (e) {
        e.preventDefault();
        var q = $.composerInput.value.trim();
        if (!q) return;
        self.askFreeText(q);
        $.composerInput.value = "";
        self.toggleSend();
      });
      $.composerInput.addEventListener("input", function () { self.toggleSend(); });
      self.toggleSend();
    }

    window.addEventListener("hashchange", function () { self.handleHash(); });
  };

  Concierge.prototype.toggleSend = function () {
    if (this.$.send) this.$.send.disabled = !this.$.composerInput.value.trim();
  };

  /* ---------- Search behaviour ---------- */
  Concierge.prototype.onSearch = function () {
    var q = this.$.search.value;
    this.$.searchWrap.classList.toggle("is-filled", !!q.trim());
    if (!q.trim()) { this.closeResults(); return; }
    this.currentResults = this.provider.search(q);
    this.renderResults(q);
    this.openResults();
  };

  Concierge.prototype.renderResults = function (q) {
    var box = this.$.results;
    box.innerHTML = "";
    this.activeIndex = -1;
    if (!this.currentResults.length) {
      box.appendChild(el("div", "dc__result-empty", "No matches for “" + esc(q) + "”. Try a diamond consultant below."));
      return;
    }
    var self = this;
    this.currentResults.forEach(function (f, i) {
      var b = el("button", "dc__result");
      b.type = "button";
      b.setAttribute("role", "option");
      b.id = self.root.id + "-opt-" + i;
      b.innerHTML = '<span>' + highlight(f.question, q) + "</span>" +
        '<span class="dc__result-cat">' + esc(f.categoryTitle) + "</span>";
      b.addEventListener("click", function () { self.ask(f.slug, true); self.closeResults(); self.clearSearch(); });
      b.addEventListener("mousemove", function () { self.setActive(i); });
      box.appendChild(b);
    });
  };

  Concierge.prototype.onSearchKey = function (e) {
    var max = this.currentResults.length;
    if (e.key === "ArrowDown") { e.preventDefault(); if (max) this.setActive((this.activeIndex + 1) % max); }
    else if (e.key === "ArrowUp") { e.preventDefault(); if (max) this.setActive((this.activeIndex - 1 + max) % max); }
    else if (e.key === "Enter") {
      e.preventDefault();
      var pick = this.currentResults[this.activeIndex] || this.currentResults[0];
      if (pick) { this.ask(pick.slug, true); this.closeResults(); this.clearSearch(); }
    } else if (e.key === "Escape") { this.clearSearch(); }
  };

  Concierge.prototype.setActive = function (i) {
    this.activeIndex = i;
    var btns = this.$.results.querySelectorAll(".dc__result");
    for (var k = 0; k < btns.length; k++) btns[k].classList.toggle("is-active", k === i);
    if (btns[i]) {
      btns[i].scrollIntoView({ block: "nearest" });
      this.$.search.setAttribute("aria-activedescendant", btns[i].id);
    }
  };

  Concierge.prototype.openResults = function () {
    if (!this.$.search.value.trim()) return;
    this.$.results.classList.add("is-open");
    this.$.search.setAttribute("aria-expanded", "true");
  };
  Concierge.prototype.closeResults = function () {
    this.$.results.classList.remove("is-open");
    this.$.search.setAttribute("aria-expanded", "false");
    this.$.search.removeAttribute("aria-activedescendant");
  };
  Concierge.prototype.clearSearch = function () {
    this.$.search.value = "";
    this.$.searchWrap.classList.remove("is-filled");
    this.closeResults();
  };

  /* ---------- Conversation ---------- */
  Concierge.prototype.showView = function (name) {
    this.$.viewHome.classList.toggle("is-active", name === "home");
    this.$.viewConvo.classList.toggle("is-active", name === "convo");
    if (this.$.convoBar) this.$.convoBar.style.display = name === "convo" ? "" : "none";
    if (this.$.composer) this.$.composer.style.display = name === "convo" ? "" : "none";
  };

  Concierge.prototype.goHome = function () {
    this.showView("home");
    if (history.replaceState) history.replaceState(null, "", location.pathname + location.search);
    this.$.scroll.scrollTop = 0;
  };

  Concierge.prototype.announce = function (msg) { if (this.$.live) this.$.live.textContent = msg; };

  Concierge.prototype.askFreeText = function (q) {
    var matches = this.provider.search(q);
    if (matches.length) { this.ask(matches[0].slug, true, q); }
    else {
      // no match -> add user bubble + graceful fallback
      this.showView("convo");
      this.addUser(q);
      this.addFallback();
      this.scrollEnd();
    }
  };

  Concierge.prototype.ask = function (slug, scroll, userText) {
    var faq = this.provider.get(slug);
    if (!faq) return;
    this.showView("convo");
    this.addUser(userText || faq.question);
    if (history.replaceState) history.replaceState(null, "", "#/q/" + slug);
    var self = this;
    var typing = this.addTyping();
    this.scrollEnd();
    var delay = this.config.reduceMotion ? 120 : 520;
    setTimeout(function () {
      typing.remove();
      self.addAnswer(faq);
      self.asked[slug] = true;
      self.announce("Answer: " + faq.question);
      if (scroll !== false) self.scrollToLast();
    }, delay);
  };

  Concierge.prototype.openCategory = function (slug) {
    var cat = this.catBySlug[slug];
    if (!cat) return;
    var faqs = this.provider.all().filter(function (f) { return f.category === slug; })
      .sort(function (a, b) { return (b.priority || 0) - (a.priority || 0); });
    if (!faqs.length) return;
    // Ask the top question and surface the rest as related
    this.ask(faqs[0].slug, true);
  };

  Concierge.prototype.addUser = function (text) {
    var m = el("div", "dc__msg dc__msg--user");
    m.appendChild(el("div", "dc__bubble-user", esc(text)));
    this.$.convo.appendChild(m);
  };

  Concierge.prototype.addTyping = function () {
    var m = el("div", "dc__msg dc__msg--bot");
    m.appendChild(el("div", "dc__typing", "<span></span><span></span><span></span>"));
    this.$.convo.appendChild(m);
    return m;
  };

  Concierge.prototype.addFallback = function () {
    var m = el("div", "dc__msg dc__msg--bot");
    var card = el("div", "dc__answer");
    card.innerHTML =
      '<div class="dc__answer-body"><p>Good question — I don’t have a saved answer for that just yet, ' +
      'but a Varniya diamond consultant will happily help.</p></div>';
    card.appendChild(this.handoff());
    m.appendChild(card);
    this.$.convo.appendChild(m);
  };

  Concierge.prototype.addAnswer = function (faq) {
    var m = el("div", "dc__msg dc__msg--bot");
    var card = el("div", "dc__answer");

    var head = el("div", "dc__answer-head");
    var body = el("div", "dc__answer-body");
    body.innerHTML = faq._html || renderRichText(faq.answer);

    if (faq.image) {
      var media = el("div", "dc__answer-media");
      var img = el("img");
      img.src = faq.image; img.alt = faq.question; img.loading = "lazy"; img.decoding = "async";
      media.appendChild(img);
      head.appendChild(body);
      head.appendChild(media);
    } else {
      head.appendChild(body);
    }
    card.appendChild(head);

    if (faq.video) {
      var v = el("div", "dc__answer-video");
      v.innerHTML = '<iframe src="' + esc(faq.video) + '" title="' + esc(faq.question) +
        '" loading="lazy" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>';
      card.appendChild(v);
    }

    if (faq.cta && faq.cta.url && faq.cta.label) {
      var cta = el("a", "dc__answer-cta");
      cta.href = faq.cta.url; cta.innerHTML = esc(faq.cta.label) + ico("arrow");
      card.appendChild(cta);
    }

    if (faq.products && faq.products.length) card.appendChild(this.products(faq.products));

    // related
    var rel = (faq.related || []).map(this.provider.get.bind(this.provider)).filter(Boolean);
    if (rel.length) card.appendChild(this.related(rel));

    // per-answer handoff
    card.appendChild(this.handoff());

    m.appendChild(card);
    this.$.convo.appendChild(m);
  };

  Concierge.prototype.products = function (list) {
    var wrap = el("div", "dc__recs");
    wrap.appendChild(el("div", "dc__block-title dc__recs-label", "You may also like"));
    var row = el("div", "dc__rec-row");
    list.forEach(function (p) {
      var a = el("a", "dc__rec"); a.href = p.url;
      a.innerHTML = (p.image ? '<img src="' + esc(p.image) + '" alt="' + esc(p.title) + '" loading="lazy">' : "") +
        '<span class="dc__rec-info"><span class="dc__rec-name">' + esc(p.title) + "</span>" +
        (p.price ? '<span class="dc__rec-price">' + esc(p.price) + "</span>" : "") + "</span>";
      row.appendChild(a);
    });
    wrap.appendChild(row);
    return wrap;
  };

  Concierge.prototype.related = function (rel) {
    var wrap = el("div", "dc__related");
    wrap.appendChild(el("div", "dc__block-title dc__related-label", "Related questions"));
    var chips = el("div", "dc__chips");
    var self = this;
    rel.forEach(function (f) {
      var b = el("button", "dc__chip"); b.type = "button";
      b.textContent = f.question;
      b.setAttribute("data-dc-ask", f.slug);
      if (self.asked[f.slug]) b.style.opacity = ".6";
      chips.appendChild(b);
    });
    wrap.appendChild(chips);
    return wrap;
  };

  Concierge.prototype.handoff = function () {
    var cfg = this.config;
    var wrap = el("div", "dc__handoff");
    wrap.appendChild(el("span", "dc__handoff-text", "Still have a question?"));
    if (cfg.whatsapp) {
      var w = el("a"); w.href = cfg.whatsapp; w.target = "_blank"; w.rel = "noopener";
      w.setAttribute("data-wa", "1");
      w.innerHTML = ico("whatsapp") + "Chat on WhatsApp";
      wrap.appendChild(w);
    }
    if (cfg.appointment) {
      var a = el("a"); a.href = cfg.appointment; a.target = "_blank"; a.rel = "noopener";
      a.innerHTML = ico("calendar") + "Book video appointment";
      wrap.appendChild(a);
    }
    if (cfg.phone) {
      var p = el("a"); p.href = "tel:" + cfg.phone.replace(/[^0-9+]/g, "");
      p.innerHTML = ico("phone") + "Call an expert";
      wrap.appendChild(p);
    }
    return wrap;
  };

  /* ---------- scrolling ---------- */
  Concierge.prototype.scrollEnd = function () {
    var s = this.$.scroll; if (s) s.scrollTop = s.scrollHeight;
  };
  Concierge.prototype.scrollToLast = function () {
    var msgs = this.$.convo.querySelectorAll(".dc__msg");
    var last = msgs[msgs.length - 1];
    if (last) last.scrollIntoView({ behavior: this.config.reduceMotion ? "auto" : "smooth", block: "nearest" });
  };

  /* ---------- deep linking ---------- */
  Concierge.prototype.handleHash = function () {
    var m = (location.hash || "").match(/^#\/q\/(.+)$/);
    if (m) {
      var slug = decodeURIComponent(m[1]);
      if (this.provider.get(slug)) { this.ask(slug, true); }
    }
  };

  /* ---------- boot ---------- */
  function boot() {
    var roots = document.querySelectorAll("[data-dc-root]");
    for (var i = 0; i < roots.length; i++) {
      if (!roots[i].__dc) roots[i].__dc = new Concierge(roots[i]);
    }
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();

  // Re-init inside Shopify theme editor
  document.addEventListener("shopify:section:load", boot);

  window.DiamondConcierge = { boot: boot, StaticProvider: StaticProvider };
})();
